/**
 * /pipeline-facility/jobs — the job register (GET /pipeline-facility/jobs,
 * fieldiq-api#1200, PF-2).
 *
 * Full width; a row opens the job on its own page. Filters (status, site,
 * customer, search) live in the URL so Back returns to the list as it was.
 * Every filter is server-side. A field hand's list is the API's — only the
 * jobs they are crewed on — and New job is not offered to them.
 *
 * Chainage arrives in the tenant's units and is labelled from `meta.units`.
 * Dates are calendar days, formatted without `new Date('YYYY-MM-DD')`.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CalendarClock, ClipboardList, HardHat, Plus, Search } from 'lucide-react';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { useAuth } from '../../contexts/AuthContext';
import { displayValue } from '../../features/fieldiq-handoff/contracts';
import { usePfJobs, usePfMeta, usePfSites } from '../../hooks/usePipelineFacility';
import { queryLoadFailure } from '../../lib/load-failure';
import { canDispatchPf, fmtNumber, pfDay, pfJobStatusSkin } from '../../lib/pipeline-facility';
import type { PfJob } from '../../types/pipeline-facility';
import { useOpenRecord } from '../downhole/recordPage';
import { PF_EYEBROW, PF_INPUT, PfBadge, PfChip, PfChipRow, PfEmpty, PfPanel, PfRegister, type PfColumn } from './PfBits';

export const chainageRange = (from: number | null, to: number | null, unit: string | null | undefined): string => {
  if (from == null && to == null) return '—';
  return `${fmtNumber(from)}–${fmtNumber(to)}${unit ? ` ${unit}` : ''}`;
};

export default function PfJobsScreen() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const openRecord = useOpenRecord();
  const [searchParams, setSearchParams] = useSearchParams();
  const status = searchParams.get('status') ?? '';
  const siteId = searchParams.get('siteId') ?? '';
  const customerId = searchParams.get('customerId') ?? '';
  const qParam = searchParams.get('q') ?? '';
  const [search, setSearch] = useState(qParam);

  const setParam = (key: string, next: string) => {
    const p = new URLSearchParams(searchParams);
    if (next) p.set(key, next);
    else p.delete(key);
    setSearchParams(p, { replace: true });
  };

  useEffect(() => {
    const t = setTimeout(() => {
      if (search.trim() !== qParam) setParam('q', search.trim());
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const catalog = usePfMeta().data?.data ?? null;
  const filters = useMemo(() => ({ status, siteId, customerId, q: qParam }), [status, siteId, customerId, qParam]);
  const filtered = !!(status || siteId || customerId || qParam);
  const listQuery = usePfJobs(filters);
  const allQuery = usePfJobs();
  const sitesQuery = usePfSites();
  const jobs = listQuery.data?.data ?? [];
  const units = listQuery.data?.meta.units ?? allQuery.data?.meta.units ?? null;
  const all = allQuery.isLoading || queryLoadFailure(allQuery) ? undefined : allQuery.data?.data;
  const countOf = (pick: (j: PfJob) => boolean) => displayValue(all ? all.filter(pick).length : undefined);
  const countsLoading = allQuery.isLoading;
  const canAdd = canDispatchPf(user?.role);

  const customers = useMemo(() => {
    const seen = new Map<string, string>();
    (all ?? []).forEach((j) => seen.set(j.customerId, j.customerName ?? '—'));
    return Array.from(seen, ([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [all]);
  const sites = useMemo(
    () => (sitesQuery.data?.data ?? []).filter((s) => !customerId || s.customerId === customerId),
    [sitesQuery.data, customerId],
  );

  const columns: PfColumn<PfJob>[] = [
    {
      key: 'number',
      header: 'Job',
      render: (j) => (
        <>
          <p className="font-mono font-extrabold text-white">{j.jobNumber || '—'}</p>
          <p className="mt-0.5 text-xs text-fieldiq-muted">{j.moduleLabel || '—'}</p>
        </>
      ),
    },
    { key: 'customer', header: 'Customer', render: (j) => j.customerName || '—' },
    {
      key: 'site',
      header: 'Site',
      render: (j) => (
        <>
          <p>{j.siteName || '—'}</p>
          <p className="mt-0.5 text-xs text-fieldiq-muted">{j.segmentLabel || j.locationName || '—'}</p>
        </>
      ),
    },
    {
      key: 'chainage',
      header: `Chainage${units?.chainage ? ` (${units.chainage})` : ''}`,
      align: 'right',
      render: (j) => <span className="font-mono">{chainageRange(j.fromChainage, j.toChainage, null)}</span>,
    },
    { key: 'start', header: 'Scheduled', render: (j) => `${pfDay(j.scheduledStart)}${j.scheduledEnd ? ` → ${pfDay(j.scheduledEnd)}` : ''}` },
    { key: 'supervisor', header: 'Supervisor', render: (j) => j.supervisorName || '—' },
    { key: 'crew', header: 'Crew', align: 'right', render: (j) => displayValue(j.crewCount ?? undefined) },
    { key: 'logs', header: 'Logs', align: 'right', render: (j) => displayValue(j.dailyLogCount ?? undefined) },
    { key: 'last', header: 'Last log', render: (j) => pfDay(j.lastLogDate) },
    { key: 'status', header: 'Status', render: (j) => <PfBadge skin={pfJobStatusSkin(j.status)} value={j.status} /> },
  ];

  return (
    <div data-testid="pf-jobs-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow={PF_EYEBROW}
        title="Jobs"
        description="Digs, facility work and construction — crew, units, rent-ins and daily logs."
        onBack={() => navigate(-1)}
        backLabel="Back"
        actions={
          canAdd ? (
            <button type="button" onClick={() => openRecord('/pipeline-facility/jobs/new')} className="fi-action fi-action--standard fi-action--primary">
              <Plus className="size-4" aria-hidden="true" />
              New job
            </button>
          ) : undefined
        }
        stats={[
          { icon: <ClipboardList className="size-4" aria-hidden="true" />, label: 'Jobs', tone: 'neutral', loading: countsLoading, value: countOf(() => true) },
          { icon: <CalendarClock className="size-4" aria-hidden="true" />, label: 'Scheduled', tone: 'info', loading: countsLoading, value: countOf((j) => j.status === 'scheduled') },
          { icon: <HardHat className="size-4" aria-hidden="true" />, label: 'In progress', tone: 'accent', loading: countsLoading, value: countOf((j) => j.status === 'in_progress') },
        ]}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          <PfChipRow label="Filter by status">
            <PfChip label="All" active={!status} onClick={() => setParam('status', '')} count={countsLoading ? undefined : countOf(() => true)} />
            {(catalog?.jobStatuses ?? []).map((s) => (
              <PfChip
                key={s.value}
                label={s.label}
                active={status === s.value}
                onClick={() => setParam('status', s.value)}
                count={countsLoading ? undefined : countOf((j) => j.status === s.value)}
              />
            ))}
          </PfChipRow>
          <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_14rem_14rem]">
            <label className="relative block">
              <span className="sr-only">Search job, customer, site, PO, AFE or location</span>
              <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-fieldiq-muted" aria-hidden="true" />
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search job, customer, site, PO, AFE or location"
                className="h-10 w-full rounded-fi-control border border-fieldiq-border bg-fieldiq-card pl-9 pr-3 text-sm text-white placeholder:text-fieldiq-muted focus:border-fi-amber focus:outline-none"
              />
            </label>
            <select className={PF_INPUT} aria-label="Filter by customer" value={customerId} onChange={(e) => setParam('customerId', e.target.value)}>
              <option value="">All customers</option>
              {customers.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
            <select className={PF_INPUT} aria-label="Filter by site" value={siteId} onChange={(e) => setParam('siteId', e.target.value)}>
              <option value="">All sites</option>
              {sites.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>

          <PfPanel
            eyebrow="Job register"
            title="Pipeline & facility jobs"
            testId="pf-job-register"
            footer={
              !listQuery.isLoading && !queryLoadFailure(listQuery) && jobs.length > 0
                ? filtered
                  ? `Showing ${jobs.length} of ${countOf(() => true)} jobs`
                  : `${jobs.length} job${jobs.length === 1 ? '' : 's'}`
                : undefined
            }
          >
            <PfRegister
              query={listQuery}
              rows={jobs}
              columns={columns}
              rail={(j) => pfJobStatusSkin(j.status).rail}
              onOpen={(j) => openRecord(`/pipeline-facility/jobs/${j.id}`)}
              subject="jobs"
              testId="pf-job"
              minWidth="min-w-[1180px]"
              empty={filtered ? <PfEmpty>No job matches these filters.</PfEmpty> : <PfEmpty>There are no pipeline or facility jobs yet.</PfEmpty>}
              card={(j) => (
                <>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate font-mono text-sm font-extrabold text-white">{j.jobNumber || '—'}</p>
                      <p className="mt-0.5 text-xs text-fieldiq-muted">
                        {j.customerName || '—'} · {j.siteName || '—'}
                      </p>
                    </div>
                    <PfBadge skin={pfJobStatusSkin(j.status)} value={j.status} />
                  </div>
                  <p className="mt-2 text-xs text-fieldiq-text">
                    {pfDay(j.scheduledStart)} · Chainage {chainageRange(j.fromChainage, j.toChainage, units?.chainage)} · Crew{' '}
                    {displayValue(j.crewCount ?? undefined)}
                  </p>
                </>
              )}
            />
          </PfPanel>
        </div>
      </section>
    </div>
  );
}
